import axios from 'axios';
import { fathomResponseHook } from './fathom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Set withCredentials globally so ALL axios calls (including direct ones) send cookies
axios.defaults.withCredentials = true;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Only buyer, agent and admin screens need a buyer/agent session. Everything
// else is public, including unknown paths (the 404) and /intermediary-dashboard
// (intermediaries carry their own bearer token, IntermediaryDashboard.js), so
// AuthProvider does not call /auth/me there and the 401 interceptor does not
// send the visitor to buyer /login. Single list, shared with AuthContext.js.
const PROTECTED_PREFIXES = ['/buyer/', '/agent/', '/admin/'];
export const isProtectedPath = (pathname) =>
  pathname === '/admin' || PROTECTED_PREFIXES.some((p) => pathname.startsWith(p));

// Auth token is now stored in httpOnly cookies (set by backend).
// The browser sends cookies automatically with withCredentials: true.
// No localStorage fallback needed - cookies are the sole auth mechanism.

// Silent refresh, scoped to one session (mid-ktc).
// AuthContext bumps the generation on every login and logout. A request remembers the
// generation it was sent in; a 401 from an older one is never refreshed or replayed, so a
// late reply to buyer A's request cannot retry it inside buyer B's session.
// A refresh sets cookies at the HTTP layer before any JS runs, so login and logout first
// call beginSessionSwitch(): it aborts the refresh in flight and waits for it to settle,
// and no new refresh starts until endSessionSwitch().
const REFRESH_TIMEOUT_MS = 10_000;
let generation = 0;
let switching = false;
let refresh = null; // { promise, controller }

export const sessionGeneration = () => generation;
export const bumpSessionGeneration = () => { generation += 1; };

export const beginSessionSwitch = () => {
  switching = true;
  if (!refresh) return Promise.resolve();
  refresh.controller.abort();
  return refresh.promise.then(() => {}, () => {});
};
export const endSessionSwitch = () => { switching = false; };

const sessionExpired = (gen) => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
  setTimeout(() => { if (gen === generation) window.location.href = '/login'; }, 100);
};

const startRefresh = ({ signOutOnFailure = true } = {}) => {
  const gen = generation;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  const promise = axios
    .post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true, signal: controller.signal })
    .catch((err) => {
      // Only a refresh that failed in the session it belongs to signs that session out.
      if (signOutOnFailure && gen === generation && !switching && !controller.signal.aborted) sessionExpired(gen);
      throw err;
    })
    .finally(() => {
      clearTimeout(timer);
      if (refresh?.promise === promise) refresh = null;
    });
  refresh = { promise, controller };
  return promise;
};

// AuthContext.ensureSession's refresh (decision 2b): the same tracked refresh, so beginSessionSwitch
// still aborts it. Resolves true when the cookies were renewed, false on failure or while a session
// switch is in progress. A refresh started here never redirects to /login.
export const refreshSession = () => {
  if (switching) return Promise.resolve(false);
  return (refresh?.promise ?? startRefresh({ signOutOnFailure: false })).then(() => true, () => false);
};

// mid-wi8v: any successful API call may have produced a funnel conversion for Fathom.
// ?. because many tests mock axios without interceptors.
axios.interceptors?.response.use(fathomResponseHook);

api.interceptors.request.use((config) => {
  if (config._gen === undefined) config._gen = generation;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const req = error.config;
    if (error.response?.status !== 401 || !req || req._retry) return Promise.reject(error);
    if (!isProtectedPath(window.location.pathname)) return Promise.reject(error);
    if (switching || req._gen !== generation) return Promise.reject(error);
    if (req.url?.includes('/auth/refresh')) {
      sessionExpired(generation);
      return Promise.reject(error);
    }
    try {
      await (refresh?.promise ?? startRefresh());
    } catch {
      return Promise.reject(error);
    }
    if (switching || req._gen !== generation) return Promise.reject(error);
    req._retry = true;
    return api(req);
  }
);
api.interceptors.response.use(fathomResponseHook); // after the 401 handler, which tests read first

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getGooglePending: () => api.get('/auth/google/pending'),
  completeGoogleRegistration: (data) => api.post('/auth/google/complete', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  updateTutorialStatus: (data) => api.put('/auth/tutorial-status', data),
  updateUserType: (userType) => api.put('/auth/user-type', { user_type: userType }),
  updateThemePreference: (value) => api.put('/auth/theme', { theme_preference: value }),
  // I-12b: password users send their password; Google-only users rely on a Google sign-in within 10 min (403 otherwise)
  deleteAccount: (password) => api.delete('/users/me', { data: password ? { password } : {} }),
};

// Buyer APIs
export const buyerAPI = {
  createProfile: (criteria) => api.post('/buyers/profile', { criteria }),
  getProfile: () => api.get('/buyers/profile'),
  updateProfile: (criteria) => api.put('/buyers/profile', { criteria }),
  getInterestedAgents: () => api.get('/buyers/interested-agents'),
  getSuggestedProperties: () => api.get('/buyers/suggested-properties'),
  updateSuggestionStatus: (submissionId, status) => 
    api.put(`/buyers/suggested-properties/${submissionId}/status?status=${status}`),
  // Enhanced property analysis
  getPropertyAnalysis: (propertyId, includeImages = false) => 
    api.get(`/properties/${propertyId}/analysis?include_images=${includeImages}`),
  // Image analysis
  getPropertyImageAnalysis: (propertyId, forceRefresh = false) =>
    api.get(`/properties/${propertyId}/image-analysis?force_refresh=${forceRefresh}`),
  // Commute zone (isochrone)
  getCommuteZone: (forceRefresh = false) =>
    api.get(`/buyers/commute-zone?force_refresh=${forceRefresh}`),
  generateCommuteZone: () =>
    api.post('/buyers/commute-zone/generate'),
};

// Properties APIs
export const propertiesAPI = {
  // User's own properties (submitted by the current user)
  getUserProperties: () => api.get('/properties/user'),
  // Platform properties (from other users)
  getPlatformProperties: (limit = 20, offset = 0) => 
    api.get(`/properties/platform?limit=${limit}&offset=${offset}`),
  // Get single property
  getProperty: (propertyId) => api.get(`/properties/${propertyId}`),
  // For buyer (legacy - gets all properties)
  // Description intelligence
  getDescriptionInsights: (propertyId) => api.post(`/properties/${propertyId}/description-insights`),
};

// Agent APIs
export const agentAPI = {
  createProfile: (profile) => api.post('/agents/profile', { profile }),
  getProfile: () => api.get('/agents/profile'),
  updateProfile: (profile) => api.put('/agents/profile', { profile }),
  getBuyers: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/agents/buyers?${params.toString()}`);
  },
  getBuyersMarketplace: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/agents/buyers/marketplace?${params.toString()}`);
  },
  // Subscription
  getSubscriptionStatus: () => api.get('/agents/subscription/status'),
  createSubscriptionCheckout: (originUrl, nif, plan) => 
    api.post('/agents/subscription/checkout', { origin_url: originUrl, nif: nif || undefined, plan: plan || 'monthly' }),
  // Property suggestions
  suggestProperty: (buyerId, data) => 
    api.post(`/agents/buyers/${buyerId}/suggest-property`, data),
  getSubmittedProperties: () => api.get('/agents/submitted-properties'),
  deleteSubmission: (submissionId) => api.delete(`/agents/submitted-properties/${submissionId}`),
  // Buyer profile & report
  getBuyerFullProfile: (buyerId) => api.get(`/agents/buyers/${buyerId}/full-profile`),
  getBuyerCommuteZone: (buyerId) => api.get(`/agents/buyers/${buyerId}/commute-zone`),
  getPortfolioMapProperties: (buyerId) => api.get(`/agents/portfolio/map-properties${buyerId ? `?buyer_id=${buyerId}` : ''}`),
  sendPortfolioProperty: (buyerId, propertyId, ownerDeclarationAccepted = false) => api.post(`/agents/buyers/${buyerId}/send-portfolio-property`, { property_id: propertyId, ...(ownerDeclarationAccepted ? { owner_declaration_accepted: true } : {}) }),
  downloadBuyerReport: (buyerId) => api.get(`/agents/buyers/${buyerId}/report`, { responseType: 'arraybuffer' }),
  // Portfolio properties
  getPortfolioProperties: () => api.get('/agent/properties'),
  createPortfolioProperty: (data) => api.post('/agent/properties', data),
  updatePortfolioProperty: (propertyId, data) => api.patch(`/agent/properties/${propertyId}`, data),
  deletePortfolioProperty: (propertyId) => api.delete(`/agent/properties/${propertyId}`),
  // Feedback insights
  getPropertyFeedbackInsights: () => api.get('/feedback/agent-insights'),
  // Journey dashboard V2
  getJourneyDashboard: () => api.get('/agents/journey-dashboard'),
  // Overview (Home tab, APS + stats + recent activity)
  getOverview: () => api.get('/agents/overview'),
  // Demand maturity (Premium-only, distribution of buyers by readiness phase)
  getDemandMaturity: () => api.get('/agents/demand-maturity'),
  // Daily digest (Premium email)
  previewDigest: () => api.get('/agents/digest/preview'),
  sendDigestNow: () => api.post('/agents/digest/send-now'),
  // Market intelligence (Premium)
  getMarketIntelligence: () => api.get('/agents/market-intelligence'),
  // Agent Copilot (Premium chat IA)
  copilotAsk: (question, session_id = null) =>
    api.post('/agents/copilot/ask', { question, session_id }),
  copilotHistory: (session_id) =>
    api.get(`/agents/copilot/history?session_id=${encodeURIComponent(session_id)}`),
  // GDPR (Agent)
  exportData: () => api.get('/agents/account/data-export'),
  requestDelete: () => api.post('/agents/account/delete-request'),
  getDeleteStatus: () => api.get('/agents/account/delete-request'),
  // Buyer profile actions (doc-aligned, p.10)
  toggleSaveBuyer: (buyerId) => api.post(`/agents/buyers/${buyerId}/save`),
  dismissBuyer: (buyerId) => api.post(`/agents/buyers/${buyerId}/dismiss`),
  // Portfolio performance per property
  getPortfolioPerformance: () => api.get('/agents/portfolio-performance'),
  // Feedback pattern analysis
  getFeedbackPatterns: () => api.get('/agents/feedback-patterns'),
  // Saved buyers (favorites)
  getSavedBuyers: () => api.get('/agent/saved-buyers'),
  saveBuyer: (buyerId, notes) => api.post('/agent/saved-buyers', { buyer_id: buyerId, notes }),
  removeSavedBuyer: (buyerId) => api.delete(`/agent/saved-buyers/${buyerId}`),
};

// Chat APIs
export const chatAPI = {
  contactAdmin: (data) => api.post('/chat/contact-admin', data),
  contactAgent: (data) => api.post('/chat/contact-agent', data),
};

// Visit APIs
export const visitAPI = {
  schedule: (data) => api.post('/visits/schedule', data),
  getAll: () => api.get('/visits'),
  confirm: (visitId, data) => api.put(`/visits/${visitId}/confirm`, data),
  proposeAlternative: (visitId, data) => api.put(`/visits/${visitId}/propose-alternative`, data),
  acceptAlternative: (visitId) => api.put(`/visits/${visitId}/accept-alternative`),
  cancel: (visitId) => api.put(`/visits/${visitId}/cancel`),
  // Buyer-side completion + post-visit feedback (unlocks D-05 / C-05).
  complete: (visitId) => api.put(`/visits/${visitId}/complete`),
  submitFeedback: (visitId, data) => api.post(`/visits/${visitId}/feedback`, data),
};

// Payment APIs
export const paymentAPI = {
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  getHistory: () => api.get('/payments/history'),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications/'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Options APIs
export const optionsAPI = {
  getLocations: () => api.get('/options/locations'),
  getPropertyTypes: () => api.get('/options/property-types'),
  getTypologies: () => api.get('/options/typologies'),
  getTimelines: () => api.get('/options/timelines'),
};

// Stats APIs
export const statsAPI = {
  getOverview: () => api.get('/stats/overview'),
};

// Invoices APIs
export const invoiceAPI = {
  getAll: () => api.get('/user/invoices'),
  getPricing: () => api.get('/pricing'),
};

export default api;
